// Dashboard widget catalog + device-local layout (T-DB2, #174).
// v2: zone-aware (main | aside) — the DEFAULT layout mirrors the original
// two-region dashboard; the newer widgets ship OFF (available in the edit
// add-tray). v1 storage is discarded on purpose: its all-on default was the
// complaint. Editing happens ON the dashboard (iOS jiggle mode).

export type WidgetSize = "S" | "M" | "L";
export type WidgetZone = "main" | "aside";

export interface WidgetEntry {
  id: string;
  /** i18n key for the widget title. */
  titleKey: string;
  /** i18n key of the source tab used as the editor group label. */
  groupKey: string;
  icon: string;
  zone: WidgetZone;
  sizes: WidgetSize[];
  defaultSize: WidgetSize;
}

export interface LayoutItem {
  id: string;
  size: WidgetSize;
}

export interface DashboardLayout {
  version: 3;
  /** Enabled widgets, in display order (both zones interleaved; zone decides container). */
  items: LayoutItem[];
  /** Explicitly disabled ids — never resurrected by normalize. */
  off: string[];
}

const STORAGE_KEY = "nelurio.dashboard.layout.v3";

export const WIDGET_CATALOG: WidgetEntry[] = [
  { id: "today", titleKey: "widget.today", groupKey: "nav.tasks", icon: "check", zone: "main", sizes: ["M", "L"], defaultSize: "L", },
  { id: "calendar", titleKey: "widget.calendar", groupKey: "nav.tasks", icon: "calendar", zone: "aside", sizes: ["M", "L"], defaultSize: "M" },
  { id: "stats", titleKey: "widget.stats", groupKey: "nav.dashboard", icon: "chart", zone: "main", sizes: ["M", "L"], defaultSize: "L" },
  { id: "courses", titleKey: "widget.courses", groupKey: "nav.courses", icon: "book", zone: "main", sizes: ["M", "L"], defaultSize: "L" },
  { id: "focus", titleKey: "widget.focus", groupKey: "nav.focus", icon: "timer", zone: "main", sizes: ["M", "L"], defaultSize: "M" },
  { id: "coach", titleKey: "widget.coach", groupKey: "nav.assistant", icon: "sparkle", zone: "main", sizes: ["M", "L"], defaultSize: "M" },
  { id: "nextExam", titleKey: "widget.nextExam", groupKey: "nav.courses", icon: "warning", zone: "aside", sizes: ["M"], defaultSize: "M" },
  { id: "weeklyFocus", titleKey: "widget.weeklyFocus", groupKey: "nav.progress", icon: "chart", zone: "aside", sizes: ["M"], defaultSize: "M" },
  { id: "record", titleKey: "widget.record", groupKey: "nav.more", icon: "sparkle", zone: "main", sizes: ["M"], defaultSize: "M" },
  { id: "flashcards", titleKey: "widget.flashcards", groupKey: "nav.courses", icon: "book", zone: "main", sizes: ["M", "L"], defaultSize: "M" },
  { id: "planner", titleKey: "widget.planner", groupKey: "nav.more", icon: "calendar", zone: "main", sizes: ["M", "L"], defaultSize: "M" },
  { id: "progress", titleKey: "widget.progress", groupKey: "nav.progress", icon: "route", zone: "main", sizes: ["M"], defaultSize: "M" },
  { id: "academic", titleKey: "widget.academic", groupKey: "nav.more", icon: "route", zone: "main", sizes: ["M"], defaultSize: "M" },
  { id: "library", titleKey: "widget.library", groupKey: "nav.library", icon: "library", zone: "main", sizes: ["M"], defaultSize: "M" },
];

/** The ORIGINAL dashboard: original widgets in the original arrangement,
 *  everything else available in the edit tray. */
export function defaultLayout(): DashboardLayout {
  const order = ["record", "coach", "stats", "courses", "today", "focus", "calendar", "nextExam", "weeklyFocus"];
  const items: LayoutItem[] = [];
  for (const id of order) {
    const entry = WIDGET_CATALOG.find((candidate) => candidate.id === id);
    if (entry) items.push({ id: entry.id, size: entry.defaultSize });
  }
  const off = WIDGET_CATALOG.filter((entry) => !order.includes(entry.id)).map((entry) => entry.id);
  return { version: 3, items, off };
}

function clampSize(entry: WidgetEntry, size: WidgetSize | undefined): WidgetSize {
  if (size && entry.sizes.includes(size)) return size;
  return entry.defaultSize;
}

/** Merge stored layout with the catalog: unknown ids dropped, missing defaults
 *  appended per zone, sizes clamped. */
export function normalizeLayout(stored: unknown): DashboardLayout {
  const entries = new Map(WIDGET_CATALOG.map((entry) => [entry.id, entry]));
  if (!stored || typeof stored !== "object" || (stored as DashboardLayout).version !== 3) {
    return defaultLayout();
  }
  const candidate = stored as DashboardLayout;
  const items: LayoutItem[] = [];
  const seen = new Set<string>();
  const off: string[] = [];
  if (Array.isArray(candidate.items)) {
    for (const item of candidate.items) {
      if (!item || typeof item.id !== "string" || !entries.has(item.id) || seen.has(item.id)) continue;
      const entry = entries.get(item.id) as WidgetEntry;
      items.push({ id: entry.id, size: clampSize(entry, item.size as WidgetSize | undefined) });
      seen.add(entry.id);
    }
  }
  if (Array.isArray(candidate.off)) {
    for (const id of candidate.off) {
      if (typeof id === "string" && entries.has(id) && !seen.has(id)) {
        off.push(id);
        seen.add(id);
      }
    }
  }
  // Fill missing ids in DEFAULT order first (original arrangement), then any
  // newer catalog widgets, so upgrades never reorder a user's board.
  for (const defaults of defaultLayout().items) {
    if (!seen.has(defaults.id)) {
      const entry = entries.get(defaults.id) as WidgetEntry;
      items.push({ id: entry.id, size: entry.defaultSize });
      seen.add(entry.id);
    }
  }
  for (const entry of WIDGET_CATALOG) {
    if (!seen.has(entry.id)) {
      items.push({ id: entry.id, size: entry.defaultSize });
      seen.add(entry.id);
    }
  }
  return { version: 3, items, off };
}

export function loadLayout(): DashboardLayout {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return normalizeLayout(JSON.parse(raw));
  } catch { /* corrupt storage falls through to defaults */ }
  return defaultLayout();
}

export function saveLayout(layout: DashboardLayout): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeLayout(layout)));
  } catch { /* storage full/blocked: layout stays in-memory */ }
}

/** Layout event so an open dashboard re-renders the moment the editor saves. */
export const LAYOUT_EVENT = "nelurio:dashboard-layout";

export function notifyLayoutChanged(): void {
  try {
    window.dispatchEvent(new Event(LAYOUT_EVENT));
  } catch { /* ignore */ }
}
